<template>
  <Transition name="slide-fade" mode="out-in">
    <section
      v-if="errorMessage && serverError"
      class="table-toolbar table-toolbar-error align-items-center gap-2"
    >
      <Icon icon="material-symbols:error-outline" :inline="true" class="flex-shrink-0" />
      <span
        class="d-inline-block text-truncate flex-shrink-1 flex-grow-0"
        style="max-width: stretch"
      >
        {{ errorMessage }}
      </span>
      <BootstrapButton
        name="dismiss"
        variant="danger"
        size="sm"
        class="flex-shrink-0"
        @click="serverError = false"
      >
        Dismiss
      </BootstrapButton>
    </section>
    <section
      v-else-if="selectedLength > 0 && !$slots['extra-toolbar-row']"
      class="table-toolbar table-toolbar-selected"
    >
      <div class="d-flex align-items-center">
        {{ selectedLength }} item{{ selectedLength > 1 ? 's' : '' }} selected
      </div>
      <div class="d-flex align-items-center">
        <BootstrapButton
          v-if="updatable && selectedLength === 1"
          name="update"
          size="sm"
          @click="$emit('update')"
        >
          Update
          <Icon icon="material-symbols:edit-square-outline" :inline="true" class="ms-1" />
        </BootstrapButton>
        <BootstrapButton v-if="deletable" name="delete" size="sm" @click="$emit('delete')">
          Delete
          <Icon icon="material-symbols:delete-outline" :inline="true" class="ms-1" />
        </BootstrapButton>
        <slot name="toolbar-actions-selected" :selected="selected"></slot>
        <BootstrapButton name="cancel" size="sm" class="ms-2" @click="$emit('deselect')">
          Cancel
        </BootstrapButton>
      </div>
    </section>
    <section
      v-else
      class="table-toolbar"
      :style="{'min-block-size': nested && selectedLength === 0 ? '0' : '3rem'}"
    >
      <div class="d-flex flex-column w-100">
        <div class="d-flex flex-grow-1 align-items-stretch">
          <slot name="toolbar-actions-left" :selected="selected" />
          <div class="d-flex align-items-stretch flex-shrink-1 flex-grow-1">
            <BootstrapFormControl
              v-if="searchable"
              v-model="searchModel"
              placeholder="Search table"
              name="search"
              only-input
              class="flex-grow-1 align-self-stretch search-input"
              size="sm"
              @enter="$emit('search', searchModel)"
              @addon-clicked="$emit('search', searchModel)"
            >
              <template #buttonContent>
                <Icon icon="material-symbols:search" />
              </template>
            </BootstrapFormControl>
          </div>
          <slot name="toolbar-actions" :selected="selected"></slot>
          <BootstrapButton
            v-if="createable"
            name="create"
            size="sm"
            class="ms-2 align-self-stretch"
            @click="$emit('create')"
          >
            {{ translate('Add') }}
            <Icon icon="material-symbols:add" :inline="true" class="ms-2" />
          </BootstrapButton>
          <IconButton
            v-if="refreshable"
            name="refresh"
            icon-name="material-symbols:refresh"
            size="md"
            variant="ghost"
            class="ms-2 align-self-stretch"
            :loading="loading"
            @click="$emit('refresh')"
          />
        </div>
        <div v-if="$slots['extra-toolbar-row']" class="d-flex align-items-stretch">
          <slot name="extra-toolbar-row" :selected="selected" />
        </div>
      </div>
    </section>
  </Transition>
</template>
<script setup lang="ts">
import {useLocalisation} from '@/composables/useLocalisation';

const {translate} = useLocalisation();
const props = defineProps<{
  selected: any[];
  searchable?: boolean;
  search: string;
  createable?: boolean;
  updatable?: boolean;
  deletable?: boolean;
  errorMessage: string;
  nested?: boolean;
  refreshable?: boolean;
  loading: boolean;
}>();
defineEmits<{
  deselect: [];
  create: [];
  update: [];
  delete: [];
  search: [search: string];
  refresh: [];
}>();
const searchModel = ref(props.search);
const selectedLength = computed(() => props.selected.length);
const serverError = ref(false);
watch(
  () => props.errorMessage,
  (newValue, oldValue) => {
    if (newValue && newValue !== oldValue) serverError.value = true;
  },
);
</script>
<style lang="scss" scoped>
.table-toolbar {
  width: 100%;
  position: relative;
  display: flex;
  justify-content: flex-end;
  inline-size: 100%;
  min-block-size: 3rem;
  padding: 0 1rem;
  align-items: start;
}

.table-toolbar-selected {
  align-items: center;
  justify-content: space-between;
  background-color: var(--bs-primary);
  color: var(--bs-light);
}

.table-toolbar-error {
  align-items: center;
  justify-content: space-between;
  background-color: var(--bs-danger);
  color: var(--bs-light);
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(20px);
  opacity: 0;
}
</style>
